import type { FactorTreeNode } from "@/lib/tool-kit/math/primes";

/**
 * Positions for drawing a factor tree.
 *
 * Each subtree is given the width of its widest level — its own label or its children
 * side by side — and children are centred under their parent within that width. Labels
 * are numbers of up to 21 digits, so widths come from the label length rather than a
 * fixed slot; nothing overlaps however lopsided the tree.
 */

export interface PlacedNode {
  key: string;
  parentKey: string | null;
  label: string;
  leaf: boolean;
  x: number;
  y: number;
  width: number;
}

export interface TreeLayout {
  nodes: PlacedNode[];
  width: number;
  height: number;
}

const CHAR_WIDTH = 8.6;
const LABEL_PADDING = 18;
const GAP = 12;
const LEVEL_HEIGHT = 58;
const TOP = 26;
const BOTTOM = 26;
const SIDE = 8;

function labelWidth(node: FactorTreeNode): number {
  return node.value.toString().length * CHAR_WIDTH + LABEL_PADDING;
}

function measure(node: FactorTreeNode, widths: Map<FactorTreeNode, number>) {
  const childWidths = node.children.map((child) => measure(child, widths));
  const childrenSpan =
    childWidths.reduce((sum, w) => sum + w, 0) +
    GAP * Math.max(0, childWidths.length - 1);
  const width = Math.max(labelWidth(node), childrenSpan);
  widths.set(node, width);
  return width;
}

export function layoutFactorTree(root: FactorTreeNode): TreeLayout {
  const widths = new Map<FactorTreeNode, number>();
  const total = measure(root, widths);
  const nodes: PlacedNode[] = [];
  let depth = 0;

  const place = (
    node: FactorTreeNode,
    left: number,
    level: number,
    key: string,
    parentKey: string | null
  ) => {
    const width = widths.get(node) ?? 0;
    depth = Math.max(depth, level);
    nodes.push({
      key,
      parentKey,
      label: node.value.toString(),
      leaf: node.children.length === 0,
      x: left + width / 2,
      y: TOP + level * LEVEL_HEIGHT,
      width: labelWidth(node),
    });
    const span =
      node.children.reduce((sum, child) => sum + (widths.get(child) ?? 0), 0) +
      GAP * Math.max(0, node.children.length - 1);
    let cursor = left + (width - span) / 2;
    for (const [index, child] of node.children.entries()) {
      place(child, cursor, level + 1, `${key}.${index}`, key);
      cursor += (widths.get(child) ?? 0) + GAP;
    }
  };

  place(root, SIDE, 0, "0", null);
  return {
    nodes,
    width: total + 2 * SIDE,
    height: TOP + depth * LEVEL_HEIGHT + BOTTOM,
  };
}
